import { Tabs } from "@mantine/core";
import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";
import { AdminOnly } from "../../shared/session/guards";
import { WidePage } from "../../shared/ui/Page";

const TABS = [
  { to: "/admin/companies", key: "admin.tab_companies" },
  { to: "/admin/settings", key: "admin.tab_settings" },
  { to: "/admin/approvals", key: "admin.tab_changes" },
  { to: "/admin/providers", key: "admin.tab_providers" },
  { to: "/admin/billing", key: "admin.tab_billing" },
  { to: "/admin/trusted-networks", key: "admin.tab_networks" },
  { to: "/admin/verification", key: "admin.tab_verification" },
  { to: "/admin/webhook-deliveries", key: "admin.tab_dead_letters" },
  { to: "/admin/payments", key: "admin.tab_payments" },
  { to: "/admin/credit-adjustments", key: "admin.tab_credit" },
  { to: "/admin/accounts", key: "admin.tab_accounts" },
  { to: "/admin/outbox", key: "admin.tab_outbox" },
  { to: "/admin/audit-events", key: "admin.tab_audit" },
  { to: "/admin/testimonials", key: "admin.tab_testimonials" },
] as const;

/** AdminLayout wraps every admin page: platform administrators only, with section tabs. */
export function AdminLayout({ title, intro, children }: { title: string; intro?: string; children: ReactNode }) {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  return (
    <AdminOnly>
      <WidePage title={title} {...(intro ? { intro } : {})}>
        <Tabs value={location.pathname} onChange={(v) => v && navigate(v)} aria-label={t("admin.title")}>
          <Tabs.List>
            {TABS.map((tab) => (
              <Tabs.Tab key={tab.to} value={tab.to}>
                {t(tab.key)}
              </Tabs.Tab>
            ))}
          </Tabs.List>
          {/* Each tab owns a panel (aria-controls must point at one); the current one holds the page. */}
          {TABS.map((tab) => (
            <Tabs.Panel key={tab.to} value={tab.to} pt="lg">
              {tab.to === location.pathname && children}
            </Tabs.Panel>
          ))}
        </Tabs>
      </WidePage>
    </AdminOnly>
  );
}
